import { Paper, Tooltip } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/styles";
import classnames from "classnames";
import * as React from "react";

import { DotLoader, Link, RelodeTheme } from "../";
import { combineStyles } from "../styles/combineStyles";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {},
            title: {
                textTransform: "lowercase",
                fontFamily: [
                    // eslint-disable-next-line quotes
                    '"hkgrotesk_regular"',
                    "Helvetica",
                    "sans-serif",
                ].join(","),
                fontSize: 20,
                lineHeight: "25px",
                fontWeight: "normal",
            },
            titleContent: {
                lineHeight: "50px",
                fontWeight: "bold",
                flexShrink: 0,
                display: "flex",
                columnGap: "4px",
                alignItems: "center",
                justifyContent: "center",
                margin: 0,
            },            
            content: {
                fontSize: 40,
                lineHeight: "50px",
                minHeight: "50px",
                fontWeight: "bold",
                flexShrink: 0,

                display: "flex",
                columnGap: "4px",
                alignItems: "center",
                justifyContent: "center",
                margin: 0,
                fontFamily: [
                    // eslint-disable-next-line quotes
                    '"hkgrotesk_bold"',
                    "Helvetica",
                    "sans-serif",
                ].join(","),
                fontStyle: "normal",
            },
            buttonBase: {
                width: "100%",
                height: "100%",
            },
            paper: {
                width: "100%",
                height: "152px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                rowGap: "8px",
                [theme.breakpoints.down("md")]: {
                    height: "100%",
                    padding: theme.spacing(2),
                },
            },
            smallTitle: {
                fontSize: 16,
                lineHeight: "22px",
                color: theme.palette.text.secondary,
                fontWeight: "normal",
                textAlign: "center",
            },
            smallContent: {
                fontSize: 28,
                fontStyle: "normal",
                lineHeight: "34px",
                minHeight: "34px",
                margin: 0,
                display: "flex",
                columnGap: "4px",
            },
            labelContent: {
                display: "flex",
                columnGap: "4px",
            },
            smallPaper: {
                height: "100px",
                minWidth: "100px",
                [theme.breakpoints.down("sm")]: {
                    height: "100%",
                },
            },
            subTitle: {
                textTransform: "lowercase",
                minHeight: "24px",
                color: theme.palette.text.secondary,
                fontSize: 14,
                lineHeight: "21px",
                paddingTop: "4px",
            },
            linkClass: {
                marginBottom: "0px",
            },
            noClick: {
                cursor: "default",
                pointerEvents: "none",
            },
            padPaper: {
                paddingTop: theme.spacing(2),
                paddingBottom: theme.spacing(2),
                boxShadow: "0 2px 4px 0 rgba(0,0,0,0.1)",
            },
        }),
    { name: "Tile" }
);

interface Props {
    id: string;
    title?: string;
    subTitle?: React.ReactNode;
    tooltip?: string;
    children: React.ReactNode;
    classes?: any;
    className?: string;
    isBusy?: boolean;
    usePaper?: boolean;
    useSubTitle?: boolean;
    showAsLink?: boolean;
    titleAdornment?: React.ReactNode;
    onClick?: () => void;
    small?: boolean;
    style?: React.CSSProperties;
}
export const Tile = (props: Props) => {
    const { className: classNameProp } = props;

    const classes = combineStyles(useStyles(props), props.classes);

    const [showTooltip, setShowToolTip] = React.useState<boolean>(false);

    const rootClass = classnames(classes.root, classNameProp);
    const paperClass = classnames(classes.paper, {
        [classes.smallPaper]: props.small,
        [classes.padPaper]: !!props.usePaper,
    });

    const contentClass = classnames(classes.content, {
        [classes.smallContent]: props.small,
    });

    const subTitleClass = classnames(classes.subTitle, {
        [classes.smallTitle]: props.small,
    });

    const titleContentClass = classnames(classes.titleContent, {
        [classes.smallContent]: props.small,
    });

    const titleClass = classnames(classes.title, {
        [classes.smallTitle]: props.small,
    });

    const linkClass = classnames(classes.linkClass);

    const renderTitle = () => (
        <div className={titleContentClass}>
            <span className={titleClass}>{props.title}</span>
            {props.titleAdornment}
        </div>
    );

    const renderSubTitle = () =>
        !!props.useSubTitle ? (
            <div className={subTitleClass}>{props.subTitle}</div>
        ) : null;

    const tile = (
        <Paper className={paperClass} elevation={!!props.usePaper ? 1 : 0}>
            {!!props.title ? renderTitle() : null}
            <div data-cy={`${props.id}-text`}>
                {props.isBusy ? (
                    <div className={contentClass}>
                        <DotLoader />
                    </div>
                ) : props.showAsLink ? (
                    <Link
                        className={linkClass}
                        highlightType="internal"
                        onClick={props.onClick}
                        usePrimaryColorLink={true}
                    >
                        <div className={contentClass}>{props.children}</div>
                    </Link>
                ) : (
                    <div className={contentClass}>{props.children}</div>
                )}
            </div>
            {renderSubTitle()}
        </Paper>
    );

    return !!props.tooltip ? (
        <Tooltip 
        placement="top" 
        title={props.tooltip}
        open={showTooltip}
        onOpen={() => setShowToolTip(true)}
        onClose={() => setShowToolTip(false)}
        enterTouchDelay={0}
        leaveTouchDelay={5000}>
            <div className={rootClass} id={props.id} style={props.style}>
                {tile}
            </div>
        </Tooltip>
    ) : (
        <div className={rootClass} id={props.id} style={props.style}>
            {tile}
        </div>
    );
};
